import * as THREE from 'three';

/**
 * Tiny rAF-driven tweener for the engine's internal animations — fill state
 * crossfades, layer opacity transitions, etc. Intentionally minimal: no
 * spring physics, no chaining, no easing zoo. One easing curve, two value
 * shapes (number, THREE.Color), explicit cancel.
 *
 * Callers manage their own tween handles. Re-tweening the same target
 * mid-flight should cancel the previous handle first — otherwise both update
 * the same value every frame and one wins arbitrarily. The PolygonFillManager
 * keeps a `Map<iso, Tween>` to enforce this.
 */

/** A live tween. `cancel()` is idempotent. */
export interface Tween {
  cancel(): void;
}

/**
 * Easing curve for engine animations. Approximates the design-system
 * `cubic-bezier(0.22, 1, 0.36, 1)` (easeOutSettle) — visually
 * indistinguishable for the 200–400ms ranges we use here. If pixel-perfect
 * match becomes important later, swap for a Bezier solver.
 */
function easeOutSettle(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

type Easing = (t: number) => number;

interface ActiveTween {
  start: number;
  duration: number;
  easing: Easing;
  step: (eased: number) => void;
  onComplete?: () => void;
  cancelled: boolean;
}

const active = new Set<ActiveTween>();
let rafHandle = 0;

function loop(now: number): void {
  for (const tween of active) {
    if (tween.cancelled) {
      active.delete(tween);
      continue;
    }
    const elapsed = now - tween.start;
    const t = Math.min(1, elapsed / tween.duration);
    tween.step(tween.easing(t));
    if (t >= 1) {
      active.delete(tween);
      tween.onComplete?.();
    }
  }
  if (active.size > 0) {
    rafHandle = requestAnimationFrame(loop);
  } else {
    rafHandle = 0;
  }
}

function schedule(tween: ActiveTween): Tween {
  active.add(tween);
  if (rafHandle === 0) {
    rafHandle = requestAnimationFrame(loop);
  }
  return {
    cancel: () => {
      tween.cancelled = true;
    },
  };
}

export interface TweenNumberOptions {
  from: number;
  to: number;
  /** Duration in milliseconds. */
  ms: number;
  easing?: Easing;
  onUpdate: (value: number) => void;
  onComplete?: () => void;
}

/** Animate a number from `from` to `to` over `ms` milliseconds. */
export function tweenNumber(options: TweenNumberOptions): Tween {
  const { from, to, ms, easing = easeOutSettle, onUpdate, onComplete } = options;
  if (ms <= 0) {
    onUpdate(to);
    onComplete?.();
    return { cancel: () => {} };
  }
  return schedule({
    start: performance.now(),
    duration: ms,
    easing,
    step: (eased) => onUpdate(from + (to - from) * eased),
    onComplete,
    cancelled: false,
  });
}

/**
 * Animate a THREE.Color in-place from its current value to `to`. Mutates the
 * passed-in color object so existing material references stay live —
 * callers don't need to reassign `material.color` after each frame.
 */
export function tweenColor(
  target: THREE.Color,
  to: THREE.ColorRepresentation,
  ms: number,
  easing: Easing = easeOutSettle,
  onComplete?: () => void,
): Tween {
  const fromR = target.r;
  const fromG = target.g;
  const fromB = target.b;
  const toColor = new THREE.Color(to);
  if (ms <= 0) {
    target.setRGB(toColor.r, toColor.g, toColor.b);
    onComplete?.();
    return { cancel: () => {} };
  }
  return schedule({
    start: performance.now(),
    duration: ms,
    easing,
    step: (eased) => {
      target.setRGB(
        fromR + (toColor.r - fromR) * eased,
        fromG + (toColor.g - fromG) * eased,
        fromB + (toColor.b - fromB) * eased,
      );
    },
    onComplete,
    cancelled: false,
  });
}
